/* eslint-disable no-restricted-imports -- ky is the bare client used for the refresh round-trip; it must NOT recurse through the auth interceptor chain */
import ky, { HTTPError } from 'ky';

import { useAuthStore } from '~/store/auth.store';

/**
 * Discriminated outcome of a token refresh:
 *  - `'refreshed'` — new tokens written to the store; the caller should retry.
 *  - `'invalid'`   — backend rejected the refresh token (401/403). Tokens have
 *                    been cleared; the caller should bounce to /login.
 *  - `'transient'` — network error or 5xx. Tokens left intact so the session
 *                    survives the backend hiccup; the caller propagates the
 *                    original failure.
 */
export type RefreshOutcome = 'refreshed' | 'invalid' | 'transient';

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

const refreshPrefixUrl = import.meta.env.DEV ? '/api' : '/api/v1/identity-service';

/**
 * Module-scoped refresh guard so concurrent 401/403s — across the ky auth
 * interceptor AND the notifications SSE stream — coalesce into a single
 * in-flight refresh instead of racing two token rotations (the second of which
 * would invalidate the first).
 */
let isRefreshing = false;
let refreshPromise: Promise<RefreshOutcome> | null = null;

async function attemptRefresh(): Promise<RefreshOutcome> {
  const { refreshToken, setTokens, clearTokens } = useAuthStore.getState();
  if (!refreshToken) {
    clearTokens();
    return 'invalid';
  }

  try {
    const res = await ky
      .post('auth/refresh', {
        prefixUrl: refreshPrefixUrl,
        json: { refreshToken },
      })
      .json<RefreshResponse>();

    setTokens(res.accessToken, res.refreshToken);
    return 'refreshed';
  } catch (err) {
    // Only an explicit "your refresh token is invalid" outcome ends the session.
    // Transient errors (5xx, network, timeout) leave the session alone so a
    // flaky backend can't log the user out.
    if (err instanceof HTTPError) {
      const { status } = err.response;
      if (status === 401 || status === 403) {
        clearTokens();
        return 'invalid';
      }
    }
    return 'transient';
  }
}

/**
 * Refresh the access token, coalescing concurrent callers onto one in-flight
 * request. Shared by the ky auth interceptor (`auth.interceptor.ts`) and the
 * notifications SSE stream (`use-notifications-stream.ts`) — both refresh on
 * 401/403 but must not stack two refreshes.
 */
export function refreshAuthToken(): Promise<RefreshOutcome> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = attemptRefresh().finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  }
  return refreshPromise ?? Promise.resolve<RefreshOutcome>('invalid');
}
